/**
 * Browser-side call recording.
 *
 * Records THREE concurrent streams for each call:
 *   - mixed:    local mic + remote audio combined via a
 *               `MediaStreamAudioDestinationNode` (used for playback).
 *   - staff:    `MediaRecorder` directly on `localStream` (used for diarized
 *               transcription).
 *   - customer: `MediaRecorder` directly on `remoteStream` (used for diarized
 *               transcription).
 *
 * All three recorders share the same picked mimeType, are started together,
 * and are stopped together. The two side-only recorders are best-effort —
 * if either fails to start or produces no data, its blob is reported as
 * `null` and the upload proceeds without it. The mixed recorder is the
 * source of truth: if it produces no blob, `stop()` resolves with `null`
 * and callers skip the upload entirely.
 *
 * Defensive by design — if `MediaRecorder` or the requested mimeType is
 * unsupported, the recorder silently no-ops so the call itself keeps working.
 */

const PREFERRED_MIME_TYPES: ReadonlyArray<string> = [
  'audio/webm;codecs=opus',
  'audio/webm',
  '',
];

interface SideTrack {
  recorder: MediaRecorder;
  chunks: Blob[];
  failed: boolean;
}

interface ActiveSession {
  // Mixed (local + remote) — primary recording, drives stop semantics.
  mixedRecorder: MediaRecorder;
  mixedChunks: Blob[];
  audioContext: AudioContext;
  destination: MediaStreamAudioDestinationNode;
  sources: MediaStreamAudioSourceNode[];

  // Side recorders — best-effort, may be null if construction failed.
  staff: SideTrack | null;
  customer: SideTrack | null;

  mimeType: string;
  startedAt: number;
  stopPromise: Promise<StopResult | null> | null;
}

export interface StopResult {
  mixed: Blob;
  staff: Blob | null;
  customer: Blob | null;
  durationMs: number;
}

function pickMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const candidate of PREFERRED_MIME_TYPES) {
    // The empty string means "let the browser pick" — always supported.
    if (candidate === '') return '';
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return null;
}

function createRecorder(stream: MediaStream, mimeType: string): MediaRecorder {
  return mimeType === ''
    ? new MediaRecorder(stream)
    : new MediaRecorder(stream, { mimeType });
}

function createSideTrack(
  stream: MediaStream,
  mimeType: string
): SideTrack | null {
  // Skip if the stream has no audio tracks — MediaRecorder will throw or
  // produce nothing useful. Treat as "side recording unavailable".
  if (stream.getAudioTracks().length === 0) return null;
  try {
    const recorder = createRecorder(stream, mimeType);
    const chunks: Blob[] = [];
    const track: SideTrack = { recorder, chunks, failed: false };
    recorder.addEventListener('dataavailable', (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        track.chunks.push(event.data);
      }
    });
    recorder.addEventListener(
      'error',
      () => {
        track.failed = true;
      },
      { once: true }
    );
    return track;
  } catch {
    return null;
  }
}

function stopSide(track: SideTrack | null): Promise<Blob | null> {
  if (!track) return Promise.resolve(null);
  return new Promise<Blob | null>((resolve) => {
    const settle = () => {
      if (track.failed || track.chunks.length === 0) {
        resolve(null);
        return;
      }
      // Caller substitutes the proper blob mime type from the session.
      resolve(new Blob(track.chunks));
    };
    track.recorder.addEventListener('stop', settle, { once: true });
    track.recorder.addEventListener(
      'error',
      () => {
        track.failed = true;
        resolve(null);
      },
      { once: true }
    );
    try {
      if (track.recorder.state !== 'inactive') {
        track.recorder.stop();
      } else {
        settle();
      }
    } catch {
      track.failed = true;
      resolve(null);
    }
  });
}

export class CallRecorder {
  private session: ActiveSession | null = null;
  private stopped = false;

  isRecording(): boolean {
    return this.session !== null && !this.stopped;
  }

  /**
   * Begin recording the mixed local + remote audio plus each side alone.
   *
   * Throws if the browser lacks `MediaRecorder` / `AudioContext` or if the
   * mixed graph fails (e.g. a stream has no audio tracks). Side recorders
   * are best-effort: if either side has no audio tracks or its
   * `MediaRecorder` constructor throws, that side is silently skipped and
   * its blob will be `null` at stop time.
   *
   * Callers should wrap in try/catch and treat thrown errors as
   * "recording disabled for this call".
   */
  start(localStream: MediaStream, remoteStream: MediaStream): void {
    if (this.session || this.stopped) return;

    const mimeType = pickMimeType();
    if (mimeType === null) {
      throw new Error('MediaRecorder is not supported in this browser');
    }

    const AudioContextCtor: typeof AudioContext | undefined =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioContextCtor) {
      throw new Error('AudioContext is not supported in this browser');
    }

    const audioContext = new AudioContextCtor();
    const destination = audioContext.createMediaStreamDestination();
    const sources: MediaStreamAudioSourceNode[] = [];

    for (const stream of [localStream, remoteStream]) {
      // createMediaStreamSource throws if the stream has no audio tracks; let
      // it bubble — the caller catches and marks recording as disabled.
      const src = audioContext.createMediaStreamSource(stream);
      src.connect(destination);
      sources.push(src);
    }

    const mixedRecorder = createRecorder(destination.stream, mimeType);
    const mixedChunks: Blob[] = [];
    mixedRecorder.addEventListener('dataavailable', (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        mixedChunks.push(event.data);
      }
    });

    // Build side recorders before starting anything so we can fail-fast on
    // the mixed graph (which is the contract). Side construction never
    // throws — failures degrade to `null`.
    const staff = createSideTrack(localStream, mimeType);
    const customer = createSideTrack(remoteStream, mimeType);

    // Start all three together. If a side fails to start, mark it failed
    // and continue — the mixed track still drives the call.
    mixedRecorder.start();
    if (staff) {
      try {
        staff.recorder.start();
      } catch {
        staff.failed = true;
      }
    }
    if (customer) {
      try {
        customer.recorder.start();
      } catch {
        customer.failed = true;
      }
    }

    this.session = {
      mixedRecorder,
      mixedChunks,
      audioContext,
      destination,
      sources,
      staff,
      customer,
      mimeType,
      startedAt: performance.now(),
      stopPromise: null,
    };
  }

  /**
   * Stop all recorders and resolve with the assembled blobs + duration.
   *
   * Idempotent: subsequent calls (or calls when never started) return null.
   * Cleans up audio graph + context + side recorders regardless of outcome.
   *
   * Returns `null` if the mixed recorder produced no chunks (treat as
   * "recording unavailable" — same behaviour as before). Otherwise returns
   * `{ mixed, staff, customer, durationMs }` where the side blobs are
   * `null` if their recorder failed or produced no data.
   */
  stop(): Promise<StopResult | null> {
    if (this.stopped) return Promise.resolve(null);
    this.stopped = true;

    const session = this.session;
    if (!session) return Promise.resolve(null);

    if (session.stopPromise) return session.stopPromise;

    const blobType =
      session.mimeType !== '' ? session.mimeType : 'audio/webm';

    const promise = new Promise<StopResult | null>((resolve) => {
      let resolved = false;

      const finish = (result: StopResult | null) => {
        if (resolved) return;
        resolved = true;
        this.cleanup(session);
        resolve(result);
      };

      const finalize = async () => {
        const durationMs = Math.max(
          0,
          Math.round(performance.now() - session.startedAt)
        );

        // Wait for both side recorders to flush in parallel with the
        // mixed-stop event we already received.
        const [staffBlobRaw, customerBlobRaw] = await Promise.all([
          stopSide(session.staff),
          stopSide(session.customer),
        ]);

        if (session.mixedChunks.length === 0) {
          finish(null);
          return;
        }

        const mixed = new Blob(session.mixedChunks, { type: blobType });
        const staffBlob =
          staffBlobRaw && staffBlobRaw.size > 0
            ? new Blob([staffBlobRaw], { type: blobType })
            : null;
        const customerBlob =
          customerBlobRaw && customerBlobRaw.size > 0
            ? new Blob([customerBlobRaw], { type: blobType })
            : null;

        finish({
          mixed,
          staff: staffBlob,
          customer: customerBlob,
          durationMs,
        });
      };

      session.mixedRecorder.addEventListener(
        'stop',
        () => {
          void finalize();
        },
        { once: true }
      );
      session.mixedRecorder.addEventListener(
        'error',
        () => {
          // Surface no error — best-effort recording. Stop side recorders
          // too so we don't leak them, then resolve null.
          void Promise.all([
            stopSide(session.staff),
            stopSide(session.customer),
          ]).finally(() => {
            finish(null);
          });
        },
        { once: true }
      );

      try {
        if (session.mixedRecorder.state !== 'inactive') {
          session.mixedRecorder.stop();
        } else {
          // Already stopped externally — finalize asynchronously so side
          // recorders still get a chance to flush.
          void finalize();
        }
      } catch {
        void Promise.all([
          stopSide(session.staff),
          stopSide(session.customer),
        ]).finally(() => {
          finish(null);
        });
      }
    });

    session.stopPromise = promise;
    return promise;
  }

  private cleanup(session: ActiveSession): void {
    for (const src of session.sources) {
      try {
        src.disconnect();
      } catch {
        /* ignore */
      }
    }
    try {
      session.destination.disconnect();
    } catch {
      /* ignore */
    }
    if (session.audioContext.state !== 'closed') {
      session.audioContext.close().catch(() => {
        /* ignore */
      });
    }
    if (this.session === session) {
      this.session = null;
    }
  }
}
